import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useAdSystems } from "hooks/useAdSystems"
import { AdCampaignsService } from "services/api-service"
import {
  AdCampaignGeoType,
  AdCampaignObjective,
  AdCreativeFormat,
  AdCreativeType,
  AddAdCampaign,
  AdGeoTarget,
  AdSystem,
  AdSystemSettings,
} from "services/api-service/types"
import { SentryService } from "services/SentryService"
import styled from "styled-components"
import { Box, Row } from "ui/components/atoms/_atoms"
import Button from "ui/components/atoms/button"
import Input from "ui/components/atoms/input"
import Select from "ui/components/atoms/select"
import Spinner from "ui/components/atoms/spinner"
import { LabelH2, LabelH3, P } from "ui/components/atoms/typography"
import Alert from "ui/components/molecules/alert"
import Modal from "ui/components/molecules/modal"
import { COUNTRIES_ARRAY } from "ui/data"
import { AD_SYSTEMS, adSystemLabel, callToActionLabel, formatLabel } from "utils/adSystems"

const OBJECTIVE: AdCampaignObjective = "lead_conversion"

const LANGUAGES: [string, string][] = [
  ["Polski", "pl"],
  ["Ukraiński", "uk"],
  ["Angielski", "en"],
  ["Niemiecki", "de"],
  ["Rosyjski", "ru"],
]

const GEO_TYPES: [string, AdCampaignGeoType][] = [
  ["Miasto", "city"],
  ["Region", "region"],
  ["Cały kraj", "country"],
]

const CREATIVE_TYPES: [string, AdCreativeType][] = [
  ["Zdjęcia", "image"],
  ["Wideo", "video"],
]

type Creative = {
  type: AdCreativeType
  mediaUrls: Partial<Record<AdCreativeFormat, string>>
  videoUrl: string
  thumbnailUrl: string
}

type LanguageVersion = {
  language: string
  geoType: AdCampaignGeoType
  geoKey: string | null
  geoName: string | null
  countryCode: string
  radiusKm: number | null
  geoQuery: string
  geoResults: AdGeoTarget[]
  headline: string
  body: string
  callToAction: string
  creatives: Creative[]
}

const emptyCreative = (type: AdCreativeType): Creative => ({
  type,
  mediaUrls: {},
  videoUrl: "",
  thumbnailUrl: "",
})

const cityRadius = (settings: AdSystemSettings, geoType: AdCampaignGeoType) =>
  geoType === "city" ? settings.minRadiusKm : null

const emptyVersion = (language: string, settings: AdSystemSettings): LanguageVersion => {
  const geoType = settings.geoTypes.includes("country") ? "country" : settings.geoTypes[0]

  return {
    language,
    geoType,
    geoKey: null,
    geoName: "Polska",
    countryCode: "PL",
    radiusKm: cityRadius(settings, geoType),
    geoQuery: "",
    geoResults: [],
    headline: "",
    body: "",
    callToAction: settings.callToActions[0],
    creatives: [emptyCreative("image")],
  }
}

const OfferDetailsCampaignForm = ({
  offerId,
  show,
  setShow,
  onCreated,
}: {
  offerId: string
  show: boolean
  setShow: (value: boolean) => void
  onCreated: () => void
}) => {
  const { data: systems } = useAdSystems()
  const [system, setSystem] = useState<AdSystem | null>(null)
  const [dailyBudget, setDailyBudget] = useState("40")
  const [spendLimit, setSpendLimit] = useState("1200")
  const [versions, setVersions] = useState<LanguageVersion[]>([])
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)

  const settings = systems?.find(item => item.system === system) ?? systems?.[0]

  useEffect(() => {
    if (settings) setVersions([emptyVersion("pl", settings)])
  }, [settings])

  const estimatedDays = useMemo(() => {
    const daily = Number(dailyBudget.replace(",", "."))
    const limit = Number(spendLimit.replace(",", "."))
    if (!daily || !limit || daily <= 0) return null
    return Math.max(1, Math.round(limit / daily))
  }, [dailyBudget, spendLimit])

  const budgetTooLow = !!settings && Number(dailyBudget.replace(",", ".")) < settings.minDailyBudget

  const update = (index: number, patch: Partial<LanguageVersion>) => {
    setVersions(current => current.map((el, i) => (i === index ? { ...el, ...patch } : el)))
  }

  const updateCreative = (index: number, creativeIndex: number, patch: Partial<Creative>) => {
    setVersions(current =>
      current.map((version, i) =>
        i === index
          ? {
              ...version,
              creatives: version.creatives.map((creative, ci) =>
                ci === creativeIndex ? { ...creative, ...patch } : creative,
              ),
            }
          : version,
      ),
    )
  }

  const updateMedia = (
    index: number,
    creativeIndex: number,
    format: AdCreativeFormat,
    url: string,
  ) => {
    setVersions(current =>
      current.map((version, i) =>
        i === index
          ? {
              ...version,
              creatives: version.creatives.map((creative, ci) =>
                ci === creativeIndex
                  ? { ...creative, mediaUrls: { ...creative.mediaUrls, [format]: url } }
                  : creative,
              ),
            }
          : version,
      ),
    )
  }

  const addCreative = (index: number, type: AdCreativeType) => {
    setVersions(current =>
      current.map((version, i) =>
        i === index
          ? { ...version, creatives: [...version.creatives, emptyCreative(type)] }
          : version,
      ),
    )
  }

  const removeCreative = (index: number, creativeIndex: number) => {
    setVersions(current =>
      current.map((version, i) =>
        i === index
          ? { ...version, creatives: version.creatives.filter((_, ci) => ci !== creativeIndex) }
          : version,
      ),
    )
  }

  const searchGeo = async (index: number, query: string) => {
    update(index, { geoQuery: query })
    if (query.length < 3) return

    if (!settings) return

    try {
      const version = versions[index]
      const geoResults = await AdCampaignsService.searchGeoTargets(
        settings.system,
        query,
        version.countryCode,
        version.geoType === "region" ? "region" : "city",
      )
      update(index, { geoResults })
    } catch (e) {
      SentryService.error("[ERROR AdCampaignsService.searchGeoTargets]:", e)
    }
  }

  const buildBody = useCallback(
    (current: AdSystemSettings): AddAdCampaign => ({
      system: current.system,
      objective: OBJECTIVE,
      dailyBudget: Number(dailyBudget.replace(",", ".")),
      spendLimit: Number(spendLimit.replace(",", ".")),
      currency: "PLN",
      segments: versions.map(version => ({
        language: version.language,
        geoType: version.geoType,
        geoKey: version.geoType === "country" ? null : version.geoKey,
        geoName: version.geoName,
        countryCode: version.countryCode,
        radiusKm:
          cityRadius(current, version.geoType) === null
            ? null
            : (version.radiusKm ?? current.minRadiusKm),
      })),
      creatives: versions.flatMap(version =>
        version.creatives.map(creative => ({
          language: version.language,
          type: creative.type,
          headline: version.headline,
          body: version.body,
          description: null,
          callToAction: version.callToAction,
          mediaUrls:
            creative.type === "video"
              ? {}
              : Object.fromEntries(
                  Object.entries(creative.mediaUrls).filter(
                    ([, url]) => url && url.trim().length > 0,
                  ),
                ),
          videoUrl: creative.type === "video" ? creative.videoUrl.trim() : null,
          thumbnailUrl:
            creative.type === "video" && creative.thumbnailUrl.trim().length > 0
              ? creative.thumbnailUrl.trim()
              : null,
        })),
      ),
    }),
    [dailyBudget, spendLimit, versions],
  )

  const submit = async () => {
    if (!settings) return

    setLoading(true)
    setError("")
    try {
      await AdCampaignsService.createCampaign(offerId, buildBody(settings))
      onCreated()
      setShow(false)
    } catch (e: any) {
      SentryService.error("[ERROR AdCampaignsService.createCampaign]:", e)
      setError(e?.response?.data?.message ?? "Nie udało się utworzyć kampanii")
    } finally {
      setLoading(false)
    }
  }

  const availableLanguages = LANGUAGES.filter(
    ([, code]) => !versions.some(version => version.language === code),
  )

  if (!settings) {
    return (
      <Modal show={show} setShow={setShow} title="Nowa kampania reklamowa">
        <Spinner />
      </Modal>
    )
  }

  const presentation = AD_SYSTEMS[settings.system]
  const geoTypes = GEO_TYPES.filter(([, geoType]) => settings.geoTypes.includes(geoType))
  const callToActions: [string, string][] = settings.callToActions.map(value => [
    callToActionLabel(value),
    value,
  ])

  return (
    <Modal show={show} setShow={setShow} title="Nowa kampania reklamowa">
      <Box>
        {error && (
          <Alert $type="error" $small $marginBottom="S">
            {error}
          </Alert>
        )}

        <P $small $marginBottom="S">
          Kampania optymalizuje pod konwersje, czyli złożone aplikacje na ofertę.
        </P>

        {(systems?.length ?? 0) > 1 && (
          <Select
            name="system"
            label="System reklamowy"
            options={(systems ?? []).map((item): [string, string] => [
              adSystemLabel(item.system),
              item.system,
            ])}
            value={settings.system}
            onChange={e => setSystem(e.value as AdSystem)}
            $marginBottom="XS"
          />
        )}

        <FieldRow>
          <Input
            name="dailyBudget"
            label="Budżet dzienny (zł)"
            value={dailyBudget}
            onChange={e => setDailyBudget(String(e.value))}
            $marginBottom="XS"
          />
          <Input
            name="spendLimit"
            label="Limit całkowity (zł)"
            value={spendLimit}
            onChange={e => setSpendLimit(String(e.value))}
            $marginBottom="XS"
          />
        </FieldRow>

        {estimatedDays && !budgetTooLow && (
          <P $small $marginBottom="S">
            {`Przy tym tempie limit wystarczy na około ${estimatedDays} dni emisji. Kampania kończy się po wydaniu limitu, nie po upływie dni.`}
          </P>
        )}

        {budgetTooLow && (
          <Alert $type="error" $small $marginBottom="S">
            {`Minimalny budżet dzienny w ${presentation.label} to ${settings.minDailyBudget} zł.`}
          </Alert>
        )}

        <LabelH2 $marginTop="S" $marginBottom="XS">
          Wersje językowe
        </LabelH2>

        {versions.map((version, index) => (
          <VersionCard key={index}>
            <Row $justifyContent="space-between" $alignItems="center" $marginBottom="XS">
              <Select
                name={`language-${index}`}
                label="Język"
                options={LANGUAGES.filter(
                  ([, code]) =>
                    code === version.language || !versions.some(other => other.language === code),
                )}
                value={version.language}
                onChange={e => update(index, { language: String(e.value) })}
              />
              {versions.length > 1 && (
                <Button
                  $type="secondary"
                  icon="trash"
                  $marginLeft="XS"
                  onClick={() => setVersions(current => current.filter((_, i) => i !== index))}
                >
                  Usuń
                </Button>
              )}
            </Row>

            <FieldRow>
              <Select
                name={`geoType-${index}`}
                label="Zasięg"
                options={geoTypes}
                value={version.geoType}
                onChange={e =>
                  update(index, {
                    geoType: e.value as AdCampaignGeoType,
                    geoKey: null,
                    geoQuery: "",
                    geoResults: [],
                    radiusKm: cityRadius(settings, e.value as AdCampaignGeoType),
                  })
                }
              />
              <Select
                name={`countryCode-${index}`}
                label="Kraj"
                options={COUNTRIES_ARRAY}
                value={version.countryCode}
                onChange={e =>
                  update(index, {
                    countryCode: String(e.value).toUpperCase(),
                    geoKey: null,
                    geoQuery: "",
                    geoResults: [],
                  })
                }
              />
            </FieldRow>

            {version.geoType !== "country" && (
              <Box $marginTop="XS">
                <Input
                  name={`geo-${index}`}
                  label={version.geoType === "city" ? "Miasto" : "Region"}
                  value={version.geoQuery}
                  onChange={e => searchGeo(index, String(e.value))}
                />
                {version.geoResults.length > 0 && (
                  <GeoResults>
                    {version.geoResults.map(result => (
                      <GeoResult
                        key={result.key}
                        onClick={() =>
                          update(index, {
                            geoKey: result.key,
                            geoName: result.name,
                            geoQuery: result.name,
                            geoResults: [],
                          })
                        }
                      >
                        <P $small>{result.name}</P>
                        {result.path && <P $small>{result.path.join(" · ")}</P>}
                      </GeoResult>
                    ))}
                  </GeoResults>
                )}
              </Box>
            )}

            {version.geoType === "city" && settings.minRadiusKm !== null && (
              <Input
                name={`radius-${index}`}
                label={`Promień (km, min. ${settings.minRadiusKm})`}
                value={String(version.radiusKm ?? settings.minRadiusKm)}
                onChange={e => update(index, { radiusKm: Number(e.value) })}
                $marginTop="XS"
              />
            )}

            <Input
              name={`headline-${index}`}
              label="Nagłówek"
              value={version.headline}
              onChange={e => update(index, { headline: String(e.value) })}
              $marginTop="S"
            />
            <Input
              name={`body-${index}`}
              label="Tekst główny"
              value={version.body}
              onChange={e => update(index, { body: String(e.value) })}
              multiline
              rows={5}
              $marginTop="XS"
            />
            <Select
              name={`cta-${index}`}
              label="Przycisk"
              options={callToActions}
              value={version.callToAction}
              onChange={e => update(index, { callToAction: String(e.value) })}
              $marginTop="XS"
            />

            <LabelH3 $marginTop="S" $marginBottom="XS">
              Kreacje
            </LabelH3>
            {presentation.creativeNote && (
              <P $small $marginBottom="XS">
                {presentation.creativeNote}
              </P>
            )}

            {version.creatives.map((creative, creativeIndex) => (
              <CreativeCard key={creativeIndex}>
                <Row $justifyContent="space-between" $alignItems="center" $marginBottom="XS">
                  <Select
                    name={`type-${index}-${creativeIndex}`}
                    label="Format"
                    options={CREATIVE_TYPES}
                    value={creative.type}
                    onChange={e =>
                      updateCreative(index, creativeIndex, {
                        type: e.value as AdCreativeType,
                      })
                    }
                  />
                  {version.creatives.length > 1 && (
                    <Button
                      $type="secondary"
                      icon="trash"
                      $marginLeft="XS"
                      onClick={() => removeCreative(index, creativeIndex)}
                    >
                      Usuń
                    </Button>
                  )}
                </Row>

                {creative.type === "image" ? (
                  <>
                    {settings.formats.map((format, formatIndex) => (
                      <Input
                        key={format}
                        name={`${format}-${index}-${creativeIndex}`}
                        label={formatLabel(
                          settings.system,
                          format,
                          format === settings.requiredFormat,
                        )}
                        value={creative.mediaUrls[format] ?? ""}
                        onChange={e => updateMedia(index, creativeIndex, format, String(e.value))}
                        $marginTop={formatIndex > 0 ? "XS" : undefined}
                      />
                    ))}
                  </>
                ) : (
                  <>
                    <Input
                      name={`video-${index}-${creativeIndex}`}
                      label="Cloudinary wideo — plik mp4 (wymagane)"
                      value={creative.videoUrl}
                      onChange={e =>
                        updateCreative(index, creativeIndex, { videoUrl: String(e.value) })
                      }
                    />
                    <Input
                      name={`thumbnail-${index}-${creativeIndex}`}
                      label="Cloudinary miniatura — klatka z filmu (opcjonalne)"
                      value={creative.thumbnailUrl}
                      onChange={e =>
                        updateCreative(index, creativeIndex, { thumbnailUrl: String(e.value) })
                      }
                      $marginTop="XS"
                    />
                  </>
                )}
              </CreativeCard>
            ))}

            <Button
              $type="secondary"
              icon="plus"
              onClick={() =>
                addCreative(
                  index,
                  version.creatives.some(creative => creative.type === "video") ? "image" : "video",
                )
              }
            >
              Dodaj kreację
            </Button>
          </VersionCard>
        ))}

        {availableLanguages.length > 0 && (
          <Button
            $type="secondary"
            icon="plus"
            onClick={() =>
              setVersions(current => [...current, emptyVersion(availableLanguages[0][1], settings)])
            }
          >
            Dodaj wersję językową
          </Button>
        )}

        <ActionsRow $marginTop="M">
          <Button icon="plus" loading={loading} disabled={budgetTooLow} onClick={submit}>
            Utwórz i uruchom
          </Button>
        </ActionsRow>
      </Box>
    </Modal>
  )
}

export default OfferDetailsCampaignForm

const FieldRow = styled(Row)`
  display: flex;
  grid-gap: ${({ theme }) => theme.space.XS};
`

const ActionsRow = styled(Row)`
  display: flex;
  justify-content: flex-end;
  grid-gap: ${({ theme }) => theme.space.XS};
`

const VersionCard = styled(Box)`
  padding: ${({ theme }) => theme.space.S};
  margin-bottom: ${({ theme }) => theme.space.XS};
  border-radius: ${({ theme }) => theme.variable.borderRadius};
  background: ${({ theme }) => theme.color.background};
`

const CreativeCard = styled(Box)`
  padding: ${({ theme }) => theme.space.S};
  margin-bottom: ${({ theme }) => theme.space.XS};
  border-radius: ${({ theme }) => theme.variable.borderRadius};
  background: ${({ theme }) => theme.color.white};
  border: 1px solid ${({ theme }) => theme.color.border};
`

const GeoResults = styled(Box)`
  border-radius: ${({ theme }) => theme.variable.borderRadius};
  background: ${({ theme }) => theme.color.white};
  border: 1px solid ${({ theme }) => theme.color.border};
`

const GeoResult = styled(Box)`
  padding: ${({ theme }) => theme.space.XS};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.color.background};
  }
`
